import { Printer, X } from "lucide-react";
import { printReceipt } from "@/lib/device";

const money = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const LABELS = { cash: "Cash", card: "Card", mobile_money: "Mobile Money (MMG)", bank_transfer: "Bank Transfer", store_credit: "Store Credit", other: "Other" };

function Row({ label, value, bold, testId }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-sm" : ""}`} data-testid={testId}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function Rule() { return <div className="border-t border-dashed border-black my-1.5" />; }

export function ZReportDoc({ r }) {
  const isZ = r.report_type === "Z";
  const variance = r.variance;
  const varianceLabel = variance === null || variance === undefined ? "—" : variance === 0 ? "BALANCED" : variance > 0 ? `OVER +${money(variance)}` : `SHORT ${money(variance)}`;
  return (
    <div id="receipt" data-testid="z-report" className="bg-white text-black rounded-lg w-full max-w-[340px] p-5 print:max-w-full mono text-xs">
      <div className="text-center mb-2">
        <div className="font-bold text-sm">{r.merchant_name}</div>
        <div>{r.store_name}</div>
        {r.store_address && <div>{r.store_address}</div>}
        <div className="font-bold text-base mt-2" data-testid="z-report-title">{isZ ? "Z-REPORT — SHIFT CLOSE" : "X-REPORT — SHIFT OPEN"}</div>
      </div>
      <Rule />
      <Row label="Register" value={r.register_number} />
      <Row label="Cashier" value={r.cashier_name} />
      <Row label="Opened" value={when(r.opened_at)} />
      <Row label={isZ ? "Closed" : "Printed"} value={when(isZ ? r.closed_at : r.generated_at)} />
      <Row label="Shift ID" value={(r.shift?.id || "").slice(0, 8)} />
      <Rule />
      <div className="font-bold mb-0.5">SALES</div>
      <Row label="Transactions" value={r.sale_count} testId="z-sale-count" />
      <Row label="Items sold" value={r.items_sold} />
      {r.offline_sales > 0 && <Row label="Offline (synced)" value={r.offline_sales} />}
      <Row label="Discounts" value={money(r.discounts)} />
      <Row label="Tax collected" value={money(r.tax_total)} />
      <Row label="GROSS SALES (GYD)" value={money(r.gross_sales)} bold testId="z-gross-sales" />
      <Rule />
      <div className="font-bold mb-0.5">BY PAYMENT METHOD</div>
      {Object.keys(r.by_payment || {}).length === 0 && <div className="text-center">No payments</div>}
      {Object.entries(r.by_payment || {}).map(([m, v]) => <Row key={m} label={LABELS[m] || m} value={money(v)} testId={`z-pay-${m}`} />)}
      {r.refunds?.count > 0 && <Row label={`Refunds (${r.refunds.count})`} value={`-${money(r.refunds.total)}`} />}
      <Rule />
      <div className="font-bold mb-0.5">CASH DRAWER</div>
      <Row label="Opening float" value={money(r.opening_cash)} />
      <Row label="+ Cash sales" value={money(r.cash_sales)} />
      {r.cash_in > 0 && <Row label="+ Cash in" value={money(r.cash_in)} />}
      {r.cash_out > 0 && <Row label="- Cash out" value={money(r.cash_out)} />}
      {r.expenses > 0 && <Row label="- Expenses" value={money(r.expenses)} />}
      {r.cash_refunds > 0 && <Row label="- Cash refunds" value={money(r.cash_refunds)} />}
      {r.change_given > 0 && <div className="text-[10px] text-gray-600">(change given {money(r.change_given)} already deducted)</div>}
      <Row label="EXPECTED CASH" value={money(r.expected_cash)} bold testId="z-expected-cash" />
      <Row label="ACTUAL COUNTED" value={r.actual_cash === null || r.actual_cash === undefined ? "—" : money(r.actual_cash)} bold testId="z-actual-cash" />
      <Row label="VARIANCE" value={varianceLabel} bold testId="z-variance" />
      {(r.movements || []).length > 0 && (<>
        <Rule />
        <div className="font-bold mb-0.5">CASH MOVEMENTS</div>
        {r.movements.map((m) => <Row key={m.id} label={`${m.type.replace("_", " ")}${m.note ? ` · ${m.note}` : ""}`} value={money(m.amount)} />)}
      </>)}
      <Rule />
      <div className="mt-3 grid grid-cols-2 gap-4 text-[10px]">
        <div><div className="border-b border-black h-6" />Cashier signature</div>
        <div><div className="border-b border-black h-6" />Manager signature</div>
      </div>
      <div className="text-center text-[9px] mt-2">Generated {when(r.generated_at)} · MatrixShopping POS</div>
    </div>
  );
}

export function ZReportModal({ report, onClose, closeLabel = "Done" }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4 print:bg-white print:block" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[92vh] overflow-y-auto"><ZReportDoc r={report} /></div>
      <div className="absolute top-4 right-4 flex gap-2 print:hidden">
        <button data-testid="z-report-print" onClick={printReceipt} className="bg-indigo-600 hover:bg-indigo-500 rounded-lg px-4 py-2 text-sm flex items-center gap-2 text-white"><Printer size={16} /> Print</button>
        <button data-testid="z-report-close" onClick={onClose} className="bg-slate-700 hover:bg-slate-600 rounded-lg px-4 py-2 text-sm text-white flex items-center gap-1"><X size={14} /> {closeLabel}</button>
      </div>
    </div>
  );
}
